import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  CircularProgress,
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { coursesAPI } from '../services/api';
import { lessonsAPI } from '../services/api';
import LessonDetail from '../components/lessons/LessonDetail';
import { toast } from 'react-toastify';
import Button from '../components/UIKit/Button';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Card from '../components/UIKit/Card';
import { useAuth } from '../context/AuthContext';
import EditIcon from '@mui/icons-material/Edit';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

const LessonDetailPage = () => {
  const { t } = useTranslation(['lessons']);
  const { courseSlug, moduleSlug, lessonSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth(); // Получаем данные пользователя из контекста
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseData, setCourseData] = useState(null);
  const [currentModule, setCurrentModule] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [moduleIndex, setModuleIndex] = useState(null);
  const [lessonIndex, setLessonIndex] = useState(null);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [locallyCompleted, setLocallyCompleted] = useState(false);
  const [isMaterialsModalOpen, setIsMaterialsModalOpen] = useState(false);
  // --- Реф для программного управления LessonDetail (онбординг) ---
  const lessonDetailRef = useRef(null);

  // Проверяем, является ли пользователь админом или учителем
  const isAdmin = user?.roles?.includes('admin');
  const isTeacher = user?.roles?.includes('teacher');
  const hasSpecialAccess = isAdmin || isTeacher;

  // Сбрасываем флаг локального завершения при смене урока
  useEffect(() => {
    setLocallyCompleted(false);
  }, [lessonSlug]);

  // Загрузка данных о курсе для навигации между уроками (только один раз при монтировании)
  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        setLoading(true);
        setError(null);

        const courseResponse = await coursesAPI.getBySlugWithProfessions(courseSlug);
        setCourseData(courseResponse.data);

        // Если пользователь авторизован, проверяем статус записи на курс
        if (user) {
          try {
            const enrollmentResponse = await coursesAPI.checkEnrollment(courseResponse.data._id);
            setIsEnrolled(enrollmentResponse.data.isEnrolled);
          } catch (enrollmentErr) {
            console.error('Ошибка при проверке записи на курс:', enrollmentErr);
          }
        }

        // Находим текущий модуль и урок
        if (courseResponse.data && courseResponse.data.modules && Array.isArray(courseResponse.data.modules)) {
          const modules = courseResponse.data.modules;

          // Находим индекс текущего модуля в массиве модулей курса
          const modIndex = modules.findIndex(
            (moduleRef) => (moduleRef.module ? moduleRef.module.slug : moduleRef.slug) === moduleSlug
          );
          if (modIndex !== -1) {
            // Получаем данные модуля из ссылки
            const moduleData = modules[modIndex].module;
            setCurrentModule(moduleData);
            setModuleIndex(modIndex);

            // Находим индекс текущего урока в модуле
            const lessons = moduleData.lessons || [];
            const lessIndex = lessons.findIndex(
              (lesson) => lesson.slug === lessonSlug
            );
            if (lessIndex !== -1) {
              const lessonFromCourse = lessons[lessIndex];
              setLessonIndex(lessIndex);

              // Загружаем полные данные урока для получения поля bookmarked
              // Используем slug вместо ID для запроса
              try {
                const lessonResponse = await lessonsAPI.getById(lessonSlug);
                const fullLessonData = lessonResponse.data.data;

                // Объединяем данные из курса с полными данными урока
                setCurrentLesson({
                  ...lessonFromCourse,
                  bookmarked: fullLessonData.bookmarked,
                  completed: fullLessonData.completed,
                  userNotes: fullLessonData.userNotes,
                });

                // Инициализируем состояние закладки
                setIsBookmarked(!!fullLessonData.bookmarked);
                // Инициализируем состояние завершения урока
                setCompleted(!!fullLessonData.completed);
              } catch (lessonErr) {
                console.error('Ошибка при загрузке данных урока:', lessonErr);
                // Если не удалось загрузить полные данные, используем данные из курса
                setCurrentLesson({
                  ...lessonFromCourse,
                  userNotes: lessonFromCourse.userNotes || "",
                });
                setIsBookmarked(!!lessonFromCourse.bookmarked);
                setCompleted(!!lessonFromCourse.completed);
              }
            } else {
              setError(t('lessonDetailPage.errors.lessonNotFound'));
            }
          } else {
            setError(t('lessonDetailPage.errors.moduleNotFound'));
          }
        } else {
          setError(t('lessonDetailPage.errors.invalidCourseData'));
        }
      } catch (err) {
        console.error('Ошибка при загрузке данных курса:', err);
        setError(
          err.response?.data?.message || t('lessonDetailPage.errors.loadError')
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [courseSlug, lessonSlug, moduleSlug, t, user]);

  // Функция для обновления данных о курсе (используем её только когда действительно нужно)
  const refreshCourseData = useCallback(async () => {
    try {
      const courseResponse = await coursesAPI.getBySlugWithProfessions(courseSlug);
      setCourseData(courseResponse.data);
      return courseResponse.data;
    } catch (err) {
      console.error('Ошибка при обновлении данных курса:', err);
      toast.error(t('lessonDetailPage.messages.courseUpdateError'));
      return null;
    }
  }, [courseSlug, t]);

  // Обработчик завершения урока
  const handleLessonComplete = useCallback(async () => {
    setLocallyCompleted(true);
    await refreshCourseData();
  }, [refreshCourseData]);

  // При изменении moduleId или lessonId обновляем текущие индексы и данные текущего модуля/урока
  useEffect(() => {
    if (courseData && courseData.modules && Array.isArray(courseData.modules)) {
      // Обновляем индекс модуля и сам модуль
      const modIndex = courseData.modules.findIndex((m) => (m.module ? m.module.slug : m.slug) === moduleSlug);
      if (modIndex !== -1) {
        setModuleIndex(modIndex);
        setCurrentModule(courseData.modules[modIndex].module);

        // Обновляем индекс урока и сам урок
        const lessons = courseData.modules[modIndex].module.lessons || [];
        const lessIndex = lessons.findIndex((l) => l.slug === lessonSlug);
        if (lessIndex !== -1) {
          setLessonIndex(lessIndex);
          setCurrentLesson({
            ...lessons[lessIndex],
            userNotes: lessons[lessIndex].userNotes || "",
          });
          // Обновляем состояние завершения урока только если урок не был завершен локально
          if (!locallyCompleted) {
            setCompleted(!!lessons[lessIndex].completed);
          }
        } else {
          setLessonIndex(null);
          setCurrentLesson(null);
        }
      } else {
        setModuleIndex(null);
        setCurrentModule(null);
        setLessonIndex(null);
        setCurrentLesson(null);
      }
    }
  }, [courseData, moduleSlug, lessonSlug, locallyCompleted]);

  // Обработчик для возврата к странице курса
  const handleBackToCourse = () => {
    // Check if user came from forum
    if (location.state?.from === 'forum' || location.state?.fromForum) {
      navigate('/forum');
    } else {
      navigate(`/courses/${courseData.slug}`);
    }
  };

  // Проверяем, есть ли тесты в текущем уроке
  const checkHasTestsInCurrentLesson = useCallback(() => {
    if (!currentLesson) return false;
    return currentLesson.tests && currentLesson.tests.length > 0;
  }, [currentLesson]);

  // Переход к следующему уроку
  const handleNextLesson = async () => {
    if (
      !courseData ||
      !courseData.modules ||
      moduleIndex === null ||
      lessonIndex === null
    )
      return;

    const currentModule = courseData.modules[moduleIndex].module;
    const currentModuleLessons = currentModule.lessons || [];

    // Проверяем, является ли текущий урок последним в модуле
    const isLastLessonInModule =
      lessonIndex === currentModuleLessons.length - 1;
    const hasTestsInCurrentLesson = checkHasTestsInCurrentLesson();

    // Для админов и учителей пропускаем проверку завершения модуля
    const shouldSkipCompletionCheck = hasSpecialAccess;

    // Если у текущего урока есть тесты, открываем модалку тестов
    if (hasTestsInCurrentLesson) {
      setIsTestModalOpen(true);
      return;
    }

    // Делаем перезапрос только если это последний урок в модуле
    // и модуль еще не отмечен как завершенный (кроме админов/учителей)
    if (
      isLastLessonInModule &&
      !currentModule.completed &&
      !shouldSkipCompletionCheck
    ) {
      try {
        // Запрашиваем свежие данные, чтобы проверить, не разблокирован ли следующий модуль
        const updatedData = await refreshCourseData();
        if (updatedData) {
        // Используем обновленные данные для навигации
        if (
          updatedData.modules[moduleIndex].module.completed ||
          shouldSkipCompletionCheck
        ) {
            // Если модуль стал завершенным или у пользователя особые права, переходим дальше
            proceedToNextModuleOrLesson(updatedData);
          } else {
            // Otherwise, show a message
            toast.info(t('lessonDetailPage.messages.completeModuleLessons'));
          }
        } else {
          // Если не удалось получить свежие данные, используем текущие
          if (currentModule.completed || shouldSkipCompletionCheck) {
            proceedToNextModuleOrLesson(courseData);
          } else {
            toast.info(t('lessonDetailPage.messages.completeModuleLessons'));
          }
        }
        return;
      } catch (error) {
        console.error('Ошибка при обновлении данных:', error);
        // Продолжаем с текущими данными
      }
    }

    // Для всех остальных случаев используем простую навигацию без запросов
    // К следующему уроку в текущем модуле
    if (lessonIndex < currentModuleLessons.length - 1) {
      const nextLesson = currentModuleLessons[lessonIndex + 1];
      const nextLessonSlug = nextLesson.slug || nextLesson._id;
      navigate(
        `/courses/${courseData.slug}/modules/${currentModule.slug}/lessons/${nextLessonSlug}`
      );
      return;
    }


    // Проверяем, завершен ли модуль для навигации к следующему модулю (кроме админов/учителей)
    if (!currentModule.completed && !shouldSkipCompletionCheck) {
      toast.info(t('lessonDetailPage.messages.completeModuleLessons'));
      return;
    }

    // Если модуль завершен или пользователь с особыми правами, переходим к следующему модулю с уроками
    proceedToNextModuleOrLesson(courseData);

    // Вспомогательная функция для поиска и перехода к следующему модулю
    function proceedToNextModuleOrLesson(data) {
      // Ищем следующий доступный модуль с уроками
      for (let i = moduleIndex + 1; i < data.modules.length; i++) {
        const nextModule = data.modules[i].module;
        // Для админов и учителей игнорируем блокировку модулей
        if (
          (shouldSkipCompletionCheck || !nextModule.locked) &&
          nextModule.lessons &&
          nextModule.lessons.length > 0
        ) {
          // Переходим к первому уроку следующего модуля
          const nextModuleId = nextModule.slug;
          const nextLesson = nextModule.lessons[0];
          const nextLessonSlug = nextLesson.slug || nextLesson._id;
          navigate(
            `/courses/${courseData.slug}/modules/${nextModuleId}/lessons/${nextLessonSlug}`
          );
          return true;
        }
      }

      // Если не нашли следующий модуль с уроками, проверяем завершен ли весь курс
      const allModulesCompleted = data.modules.every(
        (moduleRef) => moduleRef.module.completed
      );

      if (allModulesCompleted) {
        toast.success(t('lessonDetailPage.messages.courseCompleted'));
      } else {
        // Проверяем, является ли текущий модуль последним с уроками
        const isLastModuleWithLessons = !data.modules
          .slice(moduleIndex + 1)
          .some((moduleRef) => moduleRef.module.lessons && moduleRef.module.lessons.length > 0);

        if (isLastModuleWithLessons) {
          toast.info(t('lessonDetailPage.messages.endOfCourse'));
        } else {
          toast.info(t('lessonDetailPage.messages.nextModuleUnavailable'));
        }
      }

      return false;
    }
  };

  // Переход к предыдущему уроку
  const handlePrevLesson = () => {
    if (
      !courseData ||
      !courseData.modules ||
      moduleIndex === null ||
      lessonIndex === null
    )
      return;

    // Проверяем, есть ли предыдущий урок в текущем модуле
    if (lessonIndex > 0) {
      // Переходим к предыдущему уроку в текущем модуле
      const prevLesson = courseData.modules[moduleIndex].module.lessons[lessonIndex - 1];
      const prevLessonSlug = prevLesson.slug || prevLesson._id;
      navigate(
        `/courses/${courseData.slug}/modules/${currentModule.slug}/lessons/${prevLessonSlug}`
      );
      return;
    }

    // Ищем последний урок в предыдущем модуле
    for (let i = moduleIndex - 1; i >= 0; i--) {
      const prevModule = courseData.modules[i].module;
      if (prevModule.lessons && prevModule.lessons.length > 0) {
        const prevModuleId = prevModule.slug;
        const lastLessonIndex = prevModule.lessons.length - 1;
        const prevLesson = prevModule.lessons[lastLessonIndex];
        const prevLessonSlug = prevLesson.slug || prevLesson._id;
        navigate(
          `/courses/${courseData.slug}/modules/${prevModuleId}/lessons/${prevLessonSlug}`
        );
        return;
      }
    }
  };

  // Проверка наличия следующего урока или теста
  const checkHasNextLesson = useCallback(() => {
    if (
      !courseData ||
      !courseData.modules ||
      moduleIndex === null ||
      lessonIndex === null
    )
      return false;

    const currentModule = courseData.modules[moduleIndex].module;
    const currentModuleLessons = currentModule.lessons || [];

    // Проверяем, есть ли следующий урок в текущем модуле
    if (lessonIndex < currentModuleLessons.length - 1) {
      return true;
    }

    // Если это последний урок, проверяем наличие тестов в текущем уроке
    if (checkHasTestsInCurrentLesson()) {
      return true; // Тесты всегда доступны после урока
    }

    // Проверяем, есть ли уроки в следующих модулях
    for (let i = moduleIndex + 1; i < courseData.modules.length; i++) {
      if (
        courseData.modules[i].module.lessons &&
        courseData.modules[i].module.lessons.length > 0
      ) {
        return true;
      }
    }

    return false;
  }, [courseData, moduleIndex, lessonIndex, checkHasTestsInCurrentLesson]);

  // Проверка наличия предыдущего урока
  const checkHasPrevLesson = useCallback(() => {
    if (
      !courseData ||
      !courseData.modules ||
      moduleIndex === null ||
      lessonIndex === null
    )
      return false;

    // Проверяем, есть ли предыдущий урок в текущем модуле
    if (lessonIndex > 0) {
      return true;
    }

    // Проверяем, есть ли уроки в предыдущих модулях
    for (let i = moduleIndex - 1; i >= 0; i--) {
      if (
        courseData.modules[i].module.lessons &&
        courseData.modules[i].module.lessons.length > 0
      ) {
        return true;
      }
    }

    return false;
  }, [courseData, moduleIndex, lessonIndex]);


  // Функция для просмотра теста (для администраторов)
  const handlePreviewTest = (testId) => {
    setIsTestModalOpen(false);
    navigate(`/courses/${courseSlug}/modules/${moduleSlug}/tests/${testId}/preview`);
  };

  // Вспомогательная функция для проверки наличия следующего урока в текущем модуле
  const checkHasNextLessonInModule = useCallback(() => {
    if (
      !courseData ||
      !courseData.modules ||
      moduleIndex === null ||
      lessonIndex === null
    )
      return false;

    const currentModuleLessons = courseData.modules[moduleIndex].module.lessons || [];
    return lessonIndex < currentModuleLessons.length - 1;
  }, [courseData, moduleIndex, lessonIndex]);

  // Функция для пропуска теста или перехода к следующему уроку/модулю (для администраторов)
  const handleSkipTest = async () => {
    setIsTestModalOpen(false);

    // Проверяем, есть ли следующий урок в текущем модуле
    if (checkHasNextLessonInModule()) {
      // Переходим к следующему уроку в текущем модуле
      const currentModuleLessons = courseData.modules[moduleIndex].module.lessons;
      const nextLesson = currentModuleLessons[lessonIndex + 1];
      const nextLessonSlug = nextLesson.slug || nextLesson._id;
      const currentModuleData = courseData.modules[moduleIndex].module;
      navigate(
        `/courses/${courseData.slug}/modules/${currentModuleData.slug}/lessons/${nextLessonSlug}`
      );
    } else {
      // Переходим к следующему модулю
      await handleGoToNextModule();
    }
  };

  // Функция для перехода к следующему модулю из модального окна
  const handleGoToNextModule = async () => {
    if (!courseData || !courseData.modules || moduleIndex === null) return;

    // Закрываем модальное окно сразу
    setIsTestModalOpen(false);

    // Получаем текущий модуль
    const currentModule = courseData.modules[moduleIndex].module;

    // Для админов и учителей пропускаем проверку завершения модуля
    const shouldSkipCompletionCheck = hasSpecialAccess;

    // Запрашиваем свежие данные только если модуль еще не отмечен завершенным
    // и пользователь не имеет особых прав
    if (!currentModule.completed && !shouldSkipCompletionCheck) {
      try {
        // Запрашиваем свежие данные, чтобы проверить, не разблокирован ли следующий модуль
        const updatedData = await refreshCourseData();
        if (updatedData) {
          navigateToNextAvailableModule(updatedData);
        } else {
          navigateToNextAvailableModule(courseData);
        }
      } catch (error) {
        console.error('Ошибка при обновлении данных:', error);
        navigateToNextAvailableModule(courseData);
      }
    } else {
      // Если модуль уже завершен или пользователь имеет особые права, используем текущие данные
      navigateToNextAvailableModule(courseData);
    }

    // Вспомогательная функция для навигации к следующему модулю
    function navigateToNextAvailableModule(data) {
      // Ищем следующий незаблокированный модуль с уроками
      for (let i = moduleIndex + 1; i < data.modules.length; i++) {
        const nextModule = data.modules[i].module;
        // Админы и учителя могут переходить к заблокированным модулям
        if (
          (shouldSkipCompletionCheck || !nextModule.locked) &&
          nextModule.lessons &&
          nextModule.lessons.length > 0
        ) {
          // Переходим к первому уроку следующего модуля
          const nextModuleId = nextModule.slug;
          const nextLesson = nextModule.lessons[0];
          const nextLessonSlug = nextLesson.slug || nextLesson._id;
          navigate(
            `/courses/${courseData.slug}/modules/${nextModuleId}/lessons/${nextLessonSlug}`
          );
          return;
        }
      }

      // Если не нашли следующий модуль с уроками, проверяем завершен ли весь курс
      const allModulesCompleted = data.modules.every(
        (moduleRef) => moduleRef.module.completed
      );

      if (allModulesCompleted) {
        toast.success(t('lessonDetailPage.messages.courseCompleted'));
      } else {
        // Проверяем, является ли текущий модуль последним с уроками
        const isLastModuleWithLessons = !data.modules
          .slice(moduleIndex + 1)
          .some((moduleRef) => moduleRef.module.lessons && moduleRef.module.lessons.length > 0);

        if (isLastModuleWithLessons) {
          toast.info(t('lessonDetailPage.messages.endOfCourse'));
        } else {
          toast.info(t('lessonDetailPage.messages.nextModuleUnavailable'));
        }
      }
    }
  };

  // Обработчик для перехода на страницу редактирования урока
  const handleEditLesson = () => {
    navigate(`/edit-lesson/${courseData.slug}/${currentModule.slug}/${lessonSlug}`);
  };

  // Обработчик удаления урока
  const handleDeleteLesson = async () => {
    try {
      // Здесь нужно будет добавить API для удаления урока
      // await lessonsAPI.delete(currentLesson._id);
      toast.success(t('lessonDetailPage.messages.lessonDeleted'));
      // Переходим обратно к курсу
      navigate(`/courses/${courseData.slug}`);
    } catch (error) {
      console.error('Ошибка при удалении урока:', error);
      toast.error(
        error.response?.data?.message ||
          t('lessonDetailPage.messages.lessonDeleteError')
      );
    }
  };

  // Обработчик переключения закладки урока
  const handleToggleBookmark = async () => {
    if (!user) {
      toast.info(t('lessonDetailPage.messages.loginToBookmark'));
      return;
    }

    try {
      await lessonsAPI.toggleBookmark(currentLesson._id);
      const newBookmarkedState = !isBookmarked;
      setIsBookmarked(newBookmarkedState);

      // Обновляем также состояние currentLesson
      setCurrentLesson((prev) => ({
        ...prev,
        bookmarked: newBookmarkedState,
      }));

      toast.success(
        newBookmarkedState
          ? t('lessonDetailPage.messages.bookmarkAdded')
          : t('lessonDetailPage.messages.bookmarkRemoved')
      );
    } catch (error) {
      console.error('Ошибка при управлении закладкой урока:', error);
      toast.error(t('lessonDetailPage.messages.bookmarkError'));
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant="text" onClick={handleBackToCourse}>
            {t('lessonDetailPage.navigation.backToCourse')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs and Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        {/* Breadcrumb */}
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
            '& .MuiBreadcrumbs-separator': {
              color: 'text.secondary',
            },
          }}
        >
          <Link
            color="text.secondary"
            href="/courses"
            onClick={(e) => {
              e.preventDefault();
              navigate('/courses');
            }}
            sx={{
              textDecoration: 'none',
              transition: 'color 0.2s ease',
              '&:hover': {
                color: 'text.primary',
              },
            }}
          >
            {t('courses:courseDetail.breadcrumbs.courses')}
          </Link>
          <Link
            color="text.secondary"
            href={`/courses/${courseData?.slug}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/courses/${courseData?.slug}`);
            }}
            sx={{
              textDecoration: 'none',
              transition: 'color 0.2s ease',
              '&:hover': {
                color: 'text.primary',
              },
            }}
          >
            {courseData?.title}
          </Link>
          <Typography
            color="text.primary"
            sx={{
              fontWeight: 600,
              maxWidth: { xs: '200px', sm: 'none' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={currentLesson?.title}
          >
            {currentLesson?.title}
          </Typography>
        </Breadcrumbs>

        {/* Edit/Delete Buttons */}
        {user && hasSpecialAccess && (
          <Box
            sx={{ display: 'flex', gap: 2 }}
            className="lesson-edit-delete-buttons"
          >
            <Button
              onClick={handleEditLesson}
              sx={{
                minWidth: { xs: 'unset', sm: 'auto' },
              }}
            >
              <EditIcon sx={{ mr: { xs: 0, sm: 1 } }} />
              <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>
                {t('lessonDetailPage.editLesson')}
              </Typography>
            </Button>
            <Button
              color="error"
              onClick={() => setShowDeleteConfirm(true)}
              sx={{
                minWidth: { xs: 'unset', sm: 'auto' },
              }}
            >
              <DeleteIcon sx={{ mr: { xs: 0, sm: 1 } }} />
              <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>
                {t('common:actions.delete')}
              </Typography>
            </Button>
          </Box>
        )}
        {user && !hasSpecialAccess && (
          <Button
            onClick={() => navigate(`/request-lesson-changes/${lessonSlug}`)}
            startIcon={<CreateIcon />}
            variant="outlined"
            color="primary"
            className="suggest-changes-button"
          >
            {t('lessonDetailPage.suggestChanges')}
          </Button>
        )}
      </Box>

      {/* Unified Lesson Detail Component */}
      <LessonDetail
        ref={lessonDetailRef}
        lesson={currentLesson}
        courseData={courseData}
        currentModule={currentModule}
        moduleIndex={moduleIndex}
        lessonIndex={lessonIndex}
        isBookmarked={isBookmarked}
        onToggleBookmark={handleToggleBookmark}
        onScrollToReviews={() => {
          const forumSection = document.getElementById('lesson-forum-section');
          if (forumSection) {
            forumSection.scrollIntoView({ behavior: 'smooth' });
          }
        }}
        onOpenMaterials={() => setIsMaterialsModalOpen(true)}
        onCloseMaterials={() => setIsMaterialsModalOpen(false)}
        materialsModalOpen={isMaterialsModalOpen}
        showHero={true}
        showVideo={true}
        showContent={true}
        showActions={true}
        showForum={true}
        showNavigation={true}
        showNotes={true}
        showMaterials={true}
        actionsProps={{
          courseId: courseData?._id,
          moduleId: currentModule?._id,
          completed: completed,
          setCompleted: setCompleted,
          isEnrolled: isEnrolled,
          onLessonComplete: handleLessonComplete,
          hasTests: currentLesson?.tests && currentLesson.tests.length > 0,
          onStartTest: () => setIsTestModalOpen(true),
        }}
        navigationProps={{
          onBackToCourse: handleBackToCourse,
          onPrevLesson: handlePrevLesson,
          onNextLesson: handleNextLesson,
          hasPrevLesson: checkHasPrevLesson(),
          hasNextLesson: checkHasNextLesson(),
        }}
      />

      {/* Модальное окно для перехода к тесту */}
      <Dialog
        open={isTestModalOpen}
        onClose={() => setIsTestModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 2,
          },
        }}
      >
        <DialogContent
          sx={{
            textAlign: 'center',
            p: { xs: 0, sm: 3 },
            pb: { xs: 0, sm: 0 },
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'success.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
            }}
          >
            <EmojiEventsIcon sx={{ fontSize: 40, color: 'success.main' }} />
          </Box>

          {/* Определяем состояние тестов */}
          {(() => {
            if (!currentLesson || !courseData?.userProgress) return null;
            const tests = currentLesson.tests || [];
            const testResults = courseData.userProgress.testResults || {};

            // Массив: [{test, result}]
            const testsWithResults = tests.map((test) => {
              const testId =
                typeof test._id === 'string' ? test._id : test._id.toString();
              return {
                test,
                result: testResults[testId],
              };
            });

            // Первый несданный тест
            const firstUnpassed = testsWithResults.find(
              (tr) => !tr.result || !tr.result.passed
            );

            // Підраховуємо статистику тестів
            const totalTests = tests.length;
            const passedTests = testsWithResults.filter(tr => tr.result?.passed).length;
            const unpassedTests = totalTests - passedTests;

            if (firstUnpassed) {
              // Есть несданный тест
              const { test, result } = firstUnpassed;

              if (hasSpecialAccess) {
                // Для администраторов показываем информацию о тестах + список ВСЕХ тестов
                return (
                  <>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                      {t('lessonDetailPage.testModal.admin.title', {
                        title: test.title,
                      })}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {t('lessonDetailPage.testModal.admin.description')}
                      {totalTests > 1 && (
                        <>
                          <br />
                          <Box component="span" sx={{ fontWeight: 600, mt: 1, display: 'inline-block' }}>
                            {t('lessonDetailPage.testModal.admin.testsStats', {
                              total: totalTests,
                              passed: passedTests,
                              unpassed: unpassedTests
                            })}
                          </Box>
                        </>
                      )}
                    </Typography>
                    <Box>
                      {testsWithResults.map(({ test, result }) => {
                        // Определяем статус теста
                        const isPassed = result?.passed;
                        const isFailed = result && !result.passed;

                        return (
                          <Card
                            key={test._id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1.5,
                              backgroundColor: 'transparent',
                              borderLeft: 4,
                              borderColor: isPassed
                                ? 'success.main'
                                : isFailed
                                ? 'error.main'
                                : 'warning.main',
                              pl: 2,
                            }}
                          >
                            <Box sx={{ textAlign: 'left', flex: 1 }}>
                              <Typography variant="subtitle1">
                                {test.title}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: isPassed
                                    ? 'success.main'
                                    : isFailed
                                    ? 'error.main'
                                    : 'warning.main',
                                  fontWeight: 500,
                                }}
                              >
                                {isPassed
                                  ? `${t(
                                      'lessonDetailPage.testModal.score'
                                    )}: ${Math.round(result.score)} / 100 (${t(
                                      'lessonDetailPage.testModal.passingScore',
                                      { score: test.passingScore }
                                    )})`
                                  : isFailed
                                  ? `${t('lessonDetailPage.testModal.alreadyTaken', {
                                      score: Math.round(result.score),
                                    })}`
                                  : t('lessonDetailPage.testModal.notAttempted')}
                              </Typography>
                            </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handlePreviewTest(test._id)}
                            >
                              {t('lessonDetailPage.testModal.buttons.preview')}
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                // Проверяем, завершен ли урок (кроме админов)
                                if (!hasSpecialAccess) {
                                  const lessonId = typeof currentLesson._id === 'string'
                                    ? currentLesson._id
                                    : currentLesson._id.toString();
                                  const completedLessons = courseData?.userProgress?.completedLessons || [];
                                  const isLessonCompleted = completedLessons.includes(lessonId);

                                  if (!isLessonCompleted) {
                                    toast.warning(t('lessonDetailPage.testModal.lessonNotCompleted'));
                                    return;
                                  }
                                }

                                setIsTestModalOpen(false);
                                navigate(`/courses/${courseSlug}/modules/${moduleSlug}/lessons/${lessonSlug}/tests/${test._id}`);
                              }}
                            >
                              {result
                                ? t('lessonDetailPage.testModal.buttons.retake')
                                : t('lessonDetailPage.testModal.buttons.start')}
                            </Button>
                          </Box>
                        </Card>
                      );
                    })}
                    </Box>
                  </>
                );
              } else {
                // Для обычных пользователей показываем информацию о первом непройденном
                return (
                  <>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                      {t('lessonDetailPage.testModal.needToPassTest', {
                        title: test.title,
                      })}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {!result && (
                        <>
                          {t('lessonDetailPage.testModal.description')}
                          <br />
                          {t('lessonDetailPage.testModal.passingScore', {
                            score: test.passingScore,
                          })}
                          {totalTests > 1 && (
                            <>
                              <br />
                              <Box component="span" sx={{ fontWeight: 600, mt: 1, display: 'inline-block' }}>
                                {t('lessonDetailPage.testModal.testsStats', {
                                  total: totalTests,
                                  passed: passedTests,
                                  remaining: unpassedTests
                                })}
                              </Box>
                            </>
                          )}
                        </>
                      )}
                      {result && !result.passed && (
                        <>
                          {t('lessonDetailPage.testModal.alreadyTaken', {
                            score: Math.round(result.score),
                          })}
                          <br />
                          {t('lessonDetailPage.testModal.needHigherScore', {
                            score: test.passingScore,
                          })}
                          {totalTests > 1 && (
                            <>
                              <br />
                              <Box component="span" sx={{ fontWeight: 600, mt: 1, display: 'inline-block' }}>
                                {t('lessonDetailPage.testModal.testsStats', {
                                  total: totalTests,
                                  passed: passedTests,
                                  remaining: unpassedTests
                                })}
                              </Box>
                            </>
                          )}
                        </>
                      )}
                    </Typography>
                  </>
                );
              }
            } else {
              // Все тесты сданы - показываем информацию о всех тестах
              return (
                <>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('lessonDetailPage.testModal.allPassedTitle')}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {hasSpecialAccess
                      ? t('lessonDetailPage.testModal.admin.allPassedDescription')
                      : t('lessonDetailPage.testModal.allPassedDescription')}
                  </Typography>
                  <Box>
                    {testsWithResults.map(({ test, result }) => (
                      <Card
                        key={test._id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1.5,
                          backgroundColor: 'transparent',
                        }}
                      >
                        <Box sx={{ textAlign: 'left' }}>
                          <Typography variant="subtitle1">
                            {test.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {result
                              ? `${t(
                                  'lessonDetailPage.testModal.score'
                                )}: ${Math.round(result.score)} / 100 (${t(
                                  'lessonDetailPage.testModal.passingScore',
                                  { score: test.passingScore }
                                )})`
                              : t('lessonDetailPage.testModal.notAttempted')}
                          </Typography>
                          {!result && (
                            <Typography variant="body2" color="text.secondary">
                              {t('lessonDetailPage.testModal.passingScore', {
                                score: test.passingScore,
                              })}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            if (hasSpecialAccess) {
                              handlePreviewTest(test._id);
                            } else {
                              setIsTestModalOpen(false);
                              navigate(`/courses/${courseSlug}/modules/${moduleSlug}/lessons/${lessonSlug}/tests/${test._id}`);
                            }
                          }}
                        >
                          {hasSpecialAccess
                            ? t('lessonDetailPage.testModal.buttons.preview')
                            : t('lessonDetailPage.testModal.buttons.retake')}
                        </Button>
                      </Card>
                    ))}
                  </Box>
                </>
              );
            }
          })()}
        </DialogContent>

        {/* Кнопки внизу модалки - меняются в зависимости от состояния тестов */}
        <DialogActions sx={{ justifyContent: 'center', gap: 2, p: 2 }}>
          {(() => {
            if (!currentLesson || !courseData?.userProgress) return null;
            const tests = currentLesson.tests || [];
            const testResults = courseData.userProgress.testResults || {};

            // Первый несданный тест
            const firstUnpassed = tests.find((test) => {
              const testId =
                typeof test._id === 'string' ? test._id : test._id.toString();
              const result = testResults[testId];
              return !result || !result.passed;
            });

            if (firstUnpassed) {
              // Есть несданный тест - кнопки для прохождения теста
              if (hasSpecialAccess) {
                // Кнопки для администраторов - показываем кнопку для перехода к следующему уроку или модулю
                const hasNextLessonInModule = checkHasNextLessonInModule();
                
                // Проверяем, есть ли следующий модуль с уроками
                const hasNextModule = (() => {
                  for (let i = moduleIndex + 1; i < courseData.modules.length; i++) {
                    const nextModule = courseData.modules[i].module;
                    if (
                      (!nextModule.locked || hasSpecialAccess) &&
                      nextModule.lessons &&
                      nextModule.lessons.length > 0
                    ) {
                      return true;
                    }
                  }
                  return false;
                })();

                // Если есть куда переходить, показываем кнопку навигации
                if (hasNextLessonInModule || hasNextModule) {
                  return (
                    <>
                      <Button
                        variant="outlined"
                        onClick={() => setIsTestModalOpen(false)}
                        sx={{ borderRadius: '8px', px: 3 }}
                      >
                        {t('lessonDetailPage.testModal.buttons.later')}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSkipTest}
                        sx={{ borderRadius: '8px', px: 3 }}
                      >
                        {hasNextLessonInModule
                          ? t('lessonDetailPage.testModal.buttons.nextLesson')
                          : t('lessonDetailPage.testModal.buttons.nextModule')}
                      </Button>
                    </>
                  );
                } else {
                  // Это последний урок курса - показываем только кнопку закрытия
                  return (
                    <Button
                      variant="outlined"
                      onClick={() => setIsTestModalOpen(false)}
                      sx={{ borderRadius: '8px', px: 3 }}
                    >
                      {t('lessonDetailPage.testModal.buttons.later')}
                    </Button>
                  );
                }
              } else {
                // Кнопки для обычных пользователей
                // Проверяем, завершен ли урок
                const lessonId = typeof currentLesson._id === 'string'
                  ? currentLesson._id
                  : currentLesson._id.toString();
                const completedLessons = courseData?.userProgress?.completedLessons || [];
                const isLessonCompleted = completedLessons.includes(lessonId);

                return (
                  <>
                    <Button
                      variant="outlined"
                      onClick={() => setIsTestModalOpen(false)}
                      sx={{ borderRadius: '8px', px: 3 }}
                    >
                      {t('lessonDetailPage.testModal.buttons.later')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (!isLessonCompleted) {
                          toast.warning(t('lessonDetailPage.testModal.lessonNotCompleted'));
                          return;
                        }
                        setIsTestModalOpen(false);
                        navigate(`/courses/${courseSlug}/modules/${moduleSlug}/lessons/${lessonSlug}/tests/${firstUnpassed._id}`);
                      }}
                      sx={{ borderRadius: '8px', px: 3 }}
                    >
                      {testResults[firstUnpassed._id]
                        ? t('lessonDetailPage.testModal.buttons.retake')
                        : t('lessonDetailPage.testModal.buttons.start')}
                    </Button>
                  </>
                );
              }
            } else {
              // Все тесты сданы - определяем, что показывать
              // Проверяем, есть ли следующий урок в модуле или следующий модуль
              const hasNextLessonInModule = checkHasNextLessonInModule();

              // Проверяем, есть ли следующий модуль с уроками
              const hasNextModule = (() => {
                for (let i = moduleIndex + 1; i < courseData.modules.length; i++) {
                  const nextModule = courseData.modules[i].module;
                  if (
                    (!nextModule.locked || hasSpecialAccess) &&
                    nextModule.lessons &&
                    nextModule.lessons.length > 0
                  ) {
                    return true;
                  }
                }
                return false;
              })();

              // Если есть куда переходить - показываем кнопку навигации
              if (hasNextLessonInModule || hasNextModule) {
                return (
                  <>
                    <Button
                      variant="outlined"
                      onClick={() => setIsTestModalOpen(false)}
                      sx={{ borderRadius: '8px', px: 3 }}
                    >
                      {t('lessonDetailPage.testModal.buttons.later')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={hasNextLessonInModule ? handleSkipTest : handleGoToNextModule}
                      sx={{ borderRadius: '8px', px: 3 }}
                    >
                      {hasNextLessonInModule
                        ? t('lessonDetailPage.testModal.buttons.nextLesson')
                        : t('lessonDetailPage.testModal.buttons.nextModule')}
                    </Button>
                  </>
                );
              } else {
                // Это последний урок курса - показываем только кнопку закрытия
                return (
                  <Button
                    variant="outlined"
                    onClick={() => setIsTestModalOpen(false)}
                    sx={{ borderRadius: '8px', px: 3 }}
                  >
                    {t('lessonDetailPage.testModal.buttons.later')}
                  </Button>
                );
              }
            }
          })()}
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения удаления урока */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            p: 2,
          },
        }}
      >
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: 'error.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <DeleteIcon sx={{ color: 'white', fontSize: 32 }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              mb: 1,
              fontSize: '1.25rem',
              fontWeight: 600,
            }}
          >
            {t('lessonDetailPage.deleteConfirm.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('lessonDetailPage.deleteConfirm.message')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              onClick={() => setShowDeleteConfirm(false)}
              variant="outlined"
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              onClick={handleDeleteLesson}
              variant="contained"
              color="error"
              autoFocus
            >
              {t('common:actions.delete')}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Container>
  );
};

export default LessonDetailPage;
